import { CheckCircleTwoTone } from "@ant-design/icons";
import to from "await-to-js";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Select } from "../../components/Select/Select";
import { Table } from "../../components/Table/Table";
import { ModalsContext } from "../../context/modals.context";
import { centralRegistryService } from "../../modules/ministry/centralRegistry/centralRegistry.service";
import {
  GetRegistryParams,
  ObligeeRegistryConfigResponse,
  RegistryObligee,
} from "../../modules/ministry/centralRegistry/centralRegistry.types";
import { mapSelectOptions } from "../../utils/map4select";
import { writeExcel } from "../../utils/writeExcel";
import style from "./ObligeeRegistryPage.module.scss";

export interface ITableCheckMarkProps {
  isTipUvoznika: boolean;
}

export const TableCheckMark: FC<ITableCheckMarkProps> = ({ isTipUvoznika }) =>
  isTipUvoznika ? <CheckCircleTwoTone className={style.checkmark} /> : null;

export const ObligeeRegistryPage = () => {
  const { setError } = useContext(ModalsContext);
  const [registryData, setRegistryData] = useState<RegistryObligee[]>([]);
  const [registryConfig, setRegistryConfig] =
    useState<ObligeeRegistryConfigResponse | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const colDef: any = [
    {
      title: t("COMMON.INDEX"),
      width: "80px",
      dataIndex: "index",
      excelWidth: 20,
      fixed: true,
    },
    {
      title: t("COMMON.NAZIV"),
      dataIndex: "drustvoNaziv",
      width: "200px",
      fixed: true,
    },
    { title: t("COMMON.PIB"), dataIndex: "pib", width: "140px" },

    {
      title: t("COMMON.ADRESA"),
      width: "190px",
      dataIndex: "adresa",
    },

    {
      title: t("COMMON.UVOZNIK"),
      width: "30%",
      children: [
        {
          title: t("COMMON.KAFA"),
          width: 90,
          key: "kafa-uvoznik",
          render: (record: RegistryObligee) => (
            <TableCheckMark isTipUvoznika={record.kafaUvoznik} />
          ),
        },
        {
          title: t("AKCIZNE_MARKICE.ALKOHOLNA_PICA"),
          width: 110,
          key: "alkohol-uvoznik",
          render: (record: RegistryObligee) => (
            <TableCheckMark isTipUvoznika={record.alkoholUvoznik} />
          ),
        },
        {
          title: t("AKCIZNE_MARKICE.DUVANSKI_PROIZVODI"),
          width: 100,
          key: "duvan-uvoznik",
          render: (record: RegistryObligee) => (
            <TableCheckMark isTipUvoznika={record.duvanUvoznik} />
          ),
        },
      ],
    },
    {
      title: t("COMMON.PROIZVODJAC"),
      children: [
        {
          title: t("COMMON.KAFA"),
          width: 90,
          key: "kafa-zvodjac",
          render: (record: RegistryObligee) => (
            <TableCheckMark isTipUvoznika={record.kafaProizvodjac} />
          ),
        },
        {
          title: t("AKCIZNE_MARKICE.ALKOHOLNA_PICA"),
          width: 110,
          key: "alkohol-zvodjac",
          render: (record: RegistryObligee) => (
            <TableCheckMark isTipUvoznika={record.alkoholProizvodjac} />
          ),
        },
        {
          title: t("AKCIZNE_MARKICE.DUVANSKI_PROIZVODI"),
          width: 100,
          key: "duvan-zvodjac",
          render: (record: RegistryObligee) => (
            <TableCheckMark isTipUvoznika={record.duvanProizvodjac} />
          ),
        },
      ],
    },
  ];

  const excelColDefs: any[] = [
    {
      title: t("COMMON.INDEX"),
      dataIndex: "index",
      excelWidth: 20,
    },
    {
      title: t("COMMON.NAZIV"),
      dataIndex: "drustvoNaziv",
      excelWidth: 60,
    },
    { title: t("COMMON.PIB"), dataIndex: "pib", excelWidth: 16 },
    { title: t("COMMON.ADRESA"), excelWidth: 20, dataIndex: "adresa" },

    {
      title: t("COMMON.UVOZNIK") + " " + t("COMMON.KAFA"),
      excelWidth: 10,
      dataIndex: "kafaUvoznik",
      excelValueFormatter: (val: boolean) =>
        val ? t("COMMON.DA") : t("COMMON.NE"),
    },
    {
      title: t("COMMON.UVOZNIK") + " " + t("AKCIZNE_MARKICE.ALKOHOLNA_PICA"),
      excelWidth: 10,
      dataIndex: "alkoholUvoznik",
      excelValueFormatter: (val: boolean) =>
        val ? t("COMMON.DA") : t("COMMON.NE"),
    },
    {
      title:
        t("COMMON.UVOZNIK") + " " + t("AKCIZNE_MARKICE.DUVANSKI_PROIZVODI"),
      excelWidth: 10,
      dataIndex: "duvanUvoznik",
      excelValueFormatter: (val: boolean) =>
        val ? t("COMMON.DA") : t("COMMON.NE"),
    },

    {
      title: t("COMMON.KAFA"),
      excelWidth: 10,
      key: "kafaProizvodjac",
      dataIndex: "kafaProizvodjac",
      excelValueFormatter: (val: boolean) =>
        val ? t("COMMON.DA") : t("COMMON.NE"),
    },
    {
      title: t("AKCIZNE_MARKICE.ALKOHOLNA_PICA"),
      excelWidth: 10,
      key: "alkoholProizvodjac",
      dataIndex: "alkoholProizvodjac",
      excelValueFormatter: (val: boolean) =>
        val ? t("COMMON.DA") : t("COMMON.NE"),
    },
    {
      title: t("AKCIZNE_MARKICE.DUVANSKI_PROIZVODI"),
      excelWidth: 10,
      key: "duvanProizvodjac",
      dataIndex: "duvanProizvodjac",
      excelValueFormatter: (val: boolean) =>
        val ? t("COMMON.DA") : t("COMMON.NE"),
    },
  ];

  const INITIAL_PARAMS = () => ({
    pib: null,
    markicaKategorijaId: null,
    tipObveznikaId: null,
  });

  const [params, setParams] = useState(INITIAL_PARAMS());

  const getRegistryConfig = async () => {
    const [err, res] = await to(centralRegistryService.getRegistryConfig());
    if (err || !res.success) return setError(res?.errorMessage);
    setRegistryConfig(res.data);
  };

  const getRegistry = useDebouncedCallback(async () => {
    const [err, res] = await to(centralRegistryService.getRegistryData(params));
    if (err || !res.success) return setError(res?.errorMessage);
    setRegistryData(
      res.data.map((obligee, i) => ({ ...obligee, index: i + 1 }))
    );
  }, 400);

  const updateParams = (
    accessor: keyof GetRegistryParams,
    value: string | null | number
  ) => {
    setParams({ ...params, [accessor]: value });
  };

  const onRow = (row: RegistryObligee) => ({
    onClick: () => navigate(`detaljiObveznika?id=${row.id}`),
  });

  useEffect(() => {
    getRegistry();
  }, [params]);

  useEffect(() => {
    getRegistryConfig();
    getRegistry();
  }, []);

  const kategorijeOptions = mapSelectOptions({
    sourceArray: registryConfig?.markiceKategorije || [],
    labelAccessor: "name",
    valueAccessor: "id",
  });

  const statusOptions = mapSelectOptions({
    sourceArray: registryConfig?.tipoviObveznika || [],
    labelAccessor: "name",
    valueAccessor: "id",
  });

  const handleExportExcel = () => {
    writeExcel(registryData, excelColDefs, "registar_obveznika");
  };

  return (
    <div className={style.mainContainer}>
      <div id="ministryLicencesFilters">
        <h2>{t("REGISTRI.REGISTAR_OBVEZNIKA")}</h2>
        <div className={style.filterContainer}>
          {/* <Input
            allowClear
            onChange={({ target }) => updateParams("mb", target.value)}
            placeholder={t("PLACEHOLDERS.MATICNI_BROJ")}
            name="filter-mb"
            className="w-20"
          /> */}
          <Input
            allowClear
            onChange={({ target }) => updateParams("pib", target.value)}
            placeholder={t("PLACEHOLDERS.PIB")}
            name="filter-pib"
            className="w-24"
          />
          <Select
            allowClear
            className="w-24"
            onChange={(value) => updateParams("markicaKategorijaId", value)}
            placeholder={t("PLACEHOLDERS.KATEGORIJA_MARKICE")}
            options={kategorijeOptions}
            name="filter-kategorija-markice"
          />
          <Select
            allowClear
            className="w-24"
            onChange={(value) => updateParams("tipObveznikaId", value)}
            placeholder={t("PLACEHOLDERS.TIP_OBVEZNIKA")}
            options={statusOptions}
            name="filter-tip-obveznika"
          />
          <Button
            className="w-20"
            name="excel-export-exit"
            onClick={handleExportExcel}
          >
            {t("COMMON.IZVEZI_U_EXCEL")}
          </Button>
        </div>
      </div>
      <Table
        className={style.table}
        columns={colDef}
        onRow={onRow}
        scrollConfig={["#ministryLicencesFilters"]}
        rowClassName="pointer"
        dataSource={registryData}
        name="mfin-dozvole-tabela"
      />
    </div>
  );
};
